
import {
  hasPendingPasswordGrant,
  stashGrant,
  useSession,
  type SessionState,
} from '@client/shared/auth/index.ts';
import { AuthCallback, OnboardingWizard, SetPassword } from '@client/features/onboarding';
import { BillingBanner } from '@client/features/group';
import { AppShell } from './AppShell.tsx';
import { SignIn } from './SignIn.tsx';
import { SignUp } from './SignUp.tsx';
import { ForgotPassword } from './ForgotPassword.tsx';
import { Splash } from '@client/shared/design/Splash.tsx';
import { Redirect } from '@client/shared/router/Redirect.tsx';
import { PATHS } from '@client/shared/router/paths.ts';
import { useLocation, useNavigate } from '@client/shared/router/router.tsx';
import { matchRoute } from './routes.tsx';
import { validatedNext } from '@client/shared/router/validated-next.ts';

const INTERNAL_ENTRY_PATH = '/internal.html';

// /signup is a wizard path so a fresh signup's onboarding session renders the wizard in
// place (no navigation between the signup POST and the create-org step).
const WIZARD_PATHS: readonly string[] = [PATHS.onboarding, PATHS.setPassword, PATHS.signup];

function isWizardPath(pathname: string): boolean {
  return WIZARD_PATHS.includes(pathname);
}

function onboardingEmail(state: SessionState): string {
  return state.status === 'onboarding' ? state.onboarding.email : '';
}

export function App() {
  const sessionState = useSession();
  const { status, session } = sessionState;
  const location = useLocation();
  const navigate = useNavigate();

  if (location.pathname === PATHS.authCallback) {
    return <AuthCallback />;
  }

  // The STANDALONE reset/set-password surface for an authenticated email-link login (a recovery
  // reset, or a teammate invite into an existing org). SPECIAL-CASED above the session switch and
  // gated on the stashed single-use GRANT: the grant, not the cookie session, authorizes POST
  // /auth/password, and `/auth/me` can lag a tick behind the callback's POST. Rendering here means a
  // freshly-recovered user is never bounced to /login (session not yet resolved) or to the dashboard
  // (the RedirectFromWizard below) before they can set their password: the password-reset bug. A
  // self-serve onboarder instead lands on the 2-step wizard (`/onboarding` // `/set-password`),
  // which keeps its own session-gated branches below. A stray `/reset-password` hit with no pending
  // grant falls through to the normal gate (login or not-found, per session).
  if (location.pathname === PATHS.resetPassword && hasPendingPasswordGrant()) {
    return <SetPassword />;
  }

  if (status === 'loading') {
    return <Splash />;
  }
  if (status === 'platform') {
    return (
      <Redirect to={INTERNAL_ENTRY_PATH} external label="Redirecting to the admin console…" />
    );
  }
  // A setup-incomplete principal (invited, bound, no password yet) is FORCED to set-password from
  // any path. Stashing the fresh `/auth/me` grant into sessionStorage lets the grant-gated
  // SetPassword work unchanged and closes the return-after-grant-died redirect loop (ticket 03).
  if (status === 'setup-incomplete') {
    stashGrant(sessionState.setupIncomplete.passwordGrant);
    return <SetPassword />;
  }
  if (status === 'unauthenticated') {
    if (location.pathname === PATHS.login) {
      return <SignIn />;
    }
    if (location.pathname === PATHS.signup) {
      return <SignUp />;
    }
    if (location.pathname === PATHS.forgotPassword) {
      return <ForgotPassword />;
    }
    const next = encodeURIComponent(location.pathname + location.search);
    return <Redirect to={`${PATHS.login}?next=${next}`} replace />;
  }

  if (status === 'onboarding') {
    if (isWizardPath(location.pathname)) {
      // Stash the fresh `/auth/me` grant so the wizard's Step 1 set-password always has one, even on a
      // return visit that bypassed the callback stash (ticket 04).
      if (sessionState.onboarding.passwordGrant) stashGrant(sessionState.onboarding.passwordGrant);
      return <OnboardingWizard email={onboardingEmail(sessionState)} />;
    }
    return <Redirect to={PATHS.onboarding} replace label="Setting up your account…" />;
  }

  if (isWizardPath(location.pathname)) {
    return <Redirect to={PATHS.home} replace label="Setting up your dashboard…" />;
  }

  if (location.pathname === PATHS.login) {
    const next = new URLSearchParams(location.search).get('next');
    return <Redirect to={validatedNext(next)} replace />;
  }

  const { element } = matchRoute(location.pathname, session, navigate);

  return (
    <AppShell session={session} currentPath={location.pathname}>
      <BillingBanner />
      {element}
    </AppShell>
  );
}
